'use client'

import { AuthModal } from '@/components/Auth/AuthModal'
import { BillingToggle } from '@/components/Pricing/BillingToggle'
import { PlanCard } from '@/components/Pricing/PlanCard'
import { useAuthModal } from '@/hooks/useAuthModal'
import Header from '@/layout/Header'
import { fetchPaymentCheckout, fetchPaymentPortal, fetchPrice } from '@/lib/api'
import { useGetUserQuery, useGetUserQuotaQuery } from '@/lib/features/user/userApi'
import { useSearchParams } from 'next/navigation'
import { useCallback, useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { ApiPrice } from 'sprites-types/api/prices'
import { Plan, plans } from './constants'

export function PricingContent({
  minimal = false,
  pricingTitle = 'Plans & Pricing',
  pricingSubtitle = null,
}: {
  minimal?: boolean
  pricingTitle?: string
  pricingSubtitle?: string | null
}) {
  const searchParams = useSearchParams()
  const [billingInterval, setBillingInterval] = useState<'month' | 'year'>('month')
  const [prices, setPrices] = useState<Record<string, ApiPrice[]>>({})
  const [loading, setLoading] = useState(true)
  const { data: userData } = useGetUserQuery()
  const { data: quota, refetch: refetchQuota } = useGetUserQuotaQuery()
  const user = userData?.user
  const { onOpen: openAuthModal } = useAuthModal()
  const isLoggedIn = !!user && !user.isAnonymous

  useEffect(() => {
    const fetchPrices = async () => {
      // Check if we already have prices loaded
      if (Object.keys(prices).length > 0) {
        setLoading(false)
        return
      }

      setLoading(true)
      try {
        const pricePromises = [...plans]
          .filter((plan) => plan.stripeProductId)
          .map(async (plan) => {
            const price = await fetchPrice(plan.stripeProductId!)
            return [plan.stripeProductId, price]
          })

        const priceResults = await Promise.all(pricePromises)
        const priceMap = Object.fromEntries(priceResults)

        setPrices(priceMap)
      } catch (error) {
        console.error('Error fetching prices:', error)
      }
      setLoading(false)
    }

    fetchPrices()
  }, [])

  const handlePurchase = useCallback(
    async (stripePriceId: string | null) => {
      if (!isLoggedIn) {
        openAuthModal({ redirectUrl: window.location.href })
        return
      }

      if (!stripePriceId) {
        console.error('No stripe price id')
        toast.error('Oops! Something went wrong!')
        return
      }

      try {
        const response = await fetchPaymentCheckout(stripePriceId, window.location.pathname)

        // Redirect to the external checkout page
        if (response.url) {
          window.location.href = response.url
        }
      } catch (error) {
        console.error('Error:', error)
        toast.error('Oops! Something went wrong!')
      }
    },
    [quota]
  )

  const handleButtonClick = async (plan: Plan) => {
    if (plan.id === 'business') {
      window.location.href = 'mailto:founders@campfire.to'
      return
    }

    let currentPrice: ApiPrice | null = null
    if (plan.stripeProductId && prices[plan.stripeProductId]) {
      currentPrice =
        prices[plan.stripeProductId].find((p) => p.interval === billingInterval) ||
        prices[plan.stripeProductId][0]
    } else {
      console.warn('no plan found or price found', plan, prices)
      return
    }

    if (!isLoggedIn) {
      openAuthModal({
        redirectUrl: window.location.href + '?select=' + currentPrice.id,
      })
      return
    }

    if (quota?.subscription?.name !== 'Free') {
      // Redirect to portal
      const portalUrl = await fetchPaymentPortal(window.location.pathname, plan.stripeProductId!)

      if (portalUrl) {
        window.location.href = portalUrl
      }
      return
    }

    if (currentPrice) {
      handlePurchase(currentPrice.id)
    }
  }

  useEffect(() => {
    if (isLoggedIn) {
      const select = searchParams.get('select')
      if (select) {
        handlePurchase(select)
      } else if (!quota) {
        refetchQuota()
      }
    }
  }, [isLoggedIn, searchParams, handlePurchase, quota])

  const plansToShow = minimal ? plans.slice(0, 3) : plans
  const plansGridClasses = minimal
    ? 'grid mx-auto w-[100%] grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3 flex-1'
    : 'grid mx-auto w-[100%] grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-4 flex-1'

  if (loading) {
    return (
      <div className="relative flex h-full w-full flex-col">
        {!minimal && <Header />}
        <div className="flex flex-1 items-center justify-center">
          <div className="font-radio-grotesk text-xl text-white/80">Loading prices...</div>
        </div>
      </div>
    )
  }

  return (
    <>
      <div className="relative flex h-full w-full flex-col gap-y-10 p-2 lg:p-6">
        {!minimal && <Header showUpgradeBanner={false} />}

        <div className="mx-auto mb-8 flex h-full w-full max-w-[100rem] flex-1 flex-col px-4 sm:px-6 lg:px-8">
          <h1 className="!font-libre-baskerville mb-8 pt-10 text-center text-[36px] leading-none text-gray-900 md:text-[48px] xl:text-[72px]">
            {pricingTitle}
            {pricingSubtitle && (
              <div className="font-inter mt-4 text-center text-base text-gray-900">
                {pricingSubtitle}
              </div>
            )}
          </h1>
          <BillingToggle
            billingInterval={billingInterval}
            setBillingInterval={setBillingInterval}
          />

          {/* Plans grid */}
          <div className={plansGridClasses}>
            {plansToShow.map((plan) => (
              <PlanCard
                key={plan.stripeProductId}
                plan={plan}
                prices={prices}
                billingInterval={billingInterval}
                quota={quota}
                onButtonClick={handleButtonClick}
                isLoggedIn={isLoggedIn}
                minimal={minimal}
              />
            ))}
          </div>
        </div>
        {/* {!minimal && (
                    <div className="absolute left-1/2 -translate-x-1/2 bottom-[23px] flex flex-row items-start gap-[27px]">
                        <Link
                            href="/privacy"
                            className="text-white/60 hover:text-white/80 text-sm font-inter transition-colors"
                        >
                            Privacy Policy
                        </Link>
                        <Link
                            href="/terms"
                            className="text-white/60 hover:text-white/80 text-sm font-inter transition-colors"
                        >
                            Terms of Service
                        </Link>
                    </div>
                )} */}
      </div>
      <AuthModal />
    </>
  )
}
